import { isDefined } from "@clipboard-health/util-ts";
import { DocumentStatus } from "@src/appV2/Accounts/Documents/types";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BookabilityDecision, type BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { DocumentSubmissionApprovedBottomSheetContent } from "@src/appV2/redesign/Documents/Submission/ApprovedContent";
import { DocumentSubmissionInReviewBottomSheetContent } from "@src/appV2/redesign/Documents/Submission/InReviewContent";
import { DocumentSubmissionRejectedBottomSheetContent } from "@src/appV2/redesign/Documents/Submission/RejectedContent";
import {
  SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import { type DocumentSubmittedModalRoutePathParams } from "@src/appV2/redesign/ShiftDiscovery/types";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { BottomSheet, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import pluralize from "pluralize";
import { useLocation, useParams } from "react-router-dom";

import { useDefinedWorker } from "../../../../../Worker/useDefinedWorker";
// eslint-disable-next-line unicorn/prevent-abbreviations
import { usePandaDocConfig } from "../../../../Documents/Details/Workflows/PandaDoc/usePandaDocConfig";
// eslint-disable-next-line unicorn/prevent-abbreviations
import { usePandaDocSigningEnabled } from "../../../../Documents/Details/Workflows/PandaDoc/usePandaDocSigningEnabled";
import { getIncompleteRequirementPrerequisiteActions } from "../../../../Documents/utils/getIncompleteRequirementPrerequisiteActions";
import { ShiftBottomSheetSkeleton } from "../../../BottomSheet/SheetSkeleton";

interface InstantReviewShiftDocumentSubmittedBottomSheetProps {
  modalState: UseModalState;
  bookabilityStatus?: BookabilityStatusItem;
}

export function InstantReviewShiftDocumentSubmittedBottomSheet(
  props: InstantReviewShiftDocumentSubmittedBottomSheetProps
) {
  const { modalState, bookabilityStatus } = props;

  const { shiftId, requirementId, documentStatus } =
    useParams<DocumentSubmittedModalRoutePathParams>();
  const location = useLocation<{ title?: string; description?: string }>();
  const customTitle = location.state?.title;
  const customDescription = location.state?.description;

  const { navigateToModal, blockingRequirementsContext, isLoadingShiftModalsData } =
    useShiftModalsDataContext();

  const isDocumentApproved = documentStatus === DocumentStatus.APPROVED;
  const isDocumentRejected = documentStatus === DocumentStatus.REJECTED;

  const {
    visibleBlockingRequirements,
    inReviewVisibleBlockingRequirements,
    notInReviewVisibleBlockingRequirements,
    instantReviewVisibleBlockingRequirements,
    areAllVisibleBlockedDocumentsInstantReview,
  } = blockingRequirementsContext ?? {};

  const inReviewBlockingDocumentsCount = inReviewVisibleBlockingRequirements?.length ?? 0;
  const nonInReviewBlockingDocumentsCount = notInReviewVisibleBlockingRequirements?.length ?? 0;
  // eslint-disable-next-line unicorn/prevent-abbreviations
  const isPandaDocSigningEnabled = usePandaDocSigningEnabled(
    instantReviewVisibleBlockingRequirements?.[0]?.requirementId ?? ""
  );

  // eslint-disable-next-line unicorn/prevent-abbreviations
  const { redirectToSupport, useFallbackUrl } = usePandaDocConfig();

  const worker = useDefinedWorker();
  const isAnyBlockingDocumentInstantReview =
    isPandaDocSigningEnabled && (redirectToSupport || useFallbackUrl)
      ? false
      : (instantReviewVisibleBlockingRequirements?.length ?? 0) > 0;

  function getContent() {
    if (nonInReviewBlockingDocumentsCount > 0) {
      return `Complete ${nonInReviewBlockingDocumentsCount} ${
        areAllVisibleBlockedDocumentsInstantReview ? "instant review" : "more"
      } ${pluralize("document", nonInReviewBlockingDocumentsCount)} before confirming your shift.`;
    }

    if (inReviewBlockingDocumentsCount > 0) {
      return `You have documents that are pending review before you can book this shift.`;
    }

    if (bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.ALLOWED) {
      return "You can proceed to book your shift.";
    }

    return "Please complete the additional requirements to book this shift.";
  }

  const firstInstantReviewRequirement = instantReviewVisibleBlockingRequirements?.[0];
  const isPrerequisiteActionRequired = firstInstantReviewRequirement
    ? getIncompleteRequirementPrerequisiteActions({
        worker,
        workerRequirement: firstInstantReviewRequirement,
      }).length > 0
    : false;

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter
          orientation="vertical"
          onClose={() => {
            modalState.closeModal();
          }}
        >
          {!isLoadingShiftModalsData && (
            <Button
              fullWidth
              size="large"
              onClick={() => {
                logEvent(
                  APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_REVIEW_CTA_CLICKED,
                  {
                    shiftId,
                    submittedRequirementId: requirementId,
                    submittedDocumentStatus: documentStatus,
                    remainingBlockingDocumentsCount: visibleBlockingRequirements?.length,
                    isNextDocumentInstantReview: isAnyBlockingDocumentInstantReview,
                    remainingInstantReviewDocumentsCount:
                      instantReviewVisibleBlockingRequirements?.length,
                    nextInstantReviewDocumentRequirementId: isAnyBlockingDocumentInstantReview
                      ? instantReviewVisibleBlockingRequirements?.[0].requirementId
                      : undefined,
                  }
                );

                if (
                  isAnyBlockingDocumentInstantReview &&
                  !isPrerequisiteActionRequired &&
                  isDefined(instantReviewVisibleBlockingRequirements?.[0])
                ) {
                  navigateToModal(SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH, {
                    pathParams: {
                      requirementId: instantReviewVisibleBlockingRequirements?.[0]?.requirementId,
                      shiftId,
                    },
                    forceReplacePath: true,
                  });
                } else {
                  navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
                    pathParams: { shiftId },
                    forceReplacePath: true,
                  });
                }
              }}
            >
              {isAnyBlockingDocumentInstantReview && !isPrerequisiteActionRequired
                ? "Complete next document"
                : "Continue"}
            </Button>
          )}
        </DialogFooter>
      }
    >
      {isLoadingShiftModalsData ? (
        <ShiftBottomSheetSkeleton />
      ) : (
        <>
          {isDocumentApproved && (
            <DocumentSubmissionApprovedBottomSheetContent
              title={customTitle}
              description={customDescription ?? getContent()}
            />
          )}
          {isDocumentRejected && (
            <DocumentSubmissionRejectedBottomSheetContent
              title={customTitle}
              description={customDescription ?? getContent()}
            />
          )}
          {!isDocumentApproved && !isDocumentRejected && (
            <DocumentSubmissionInReviewBottomSheetContent
              title={customTitle}
              description={customDescription ?? getContent()}
            />
          )}
        </>
      )}
    </BottomSheet>
  );
}
